import React, { useEffect, useRef, useState } from "react"
import * as S from "./styled"
import PropTypes from "prop-types"
import useMedia from "use-media"
import { size } from "../../theme/devices"
import { ReactComponent as Arrow } from "../../assets/arrow-right.svg"
import ProjectCardCarouselItem from "./ProjectCard"
import GridLayout from "../../layouts/GridLayout"
import TeamMemberCard from "./TeamMemberCard"
import Slide from "./Slide"
import Pager from "../Pager"
import CarouselCard from "./CarouselCard"

const RenderItem = ({ item, isDesktop, type }) => {
  switch (type) {
    case "articles":
      return <ProjectCardCarouselItem item={item} isDesktop={isDesktop} />
    case "people":
      return <TeamMemberCard item={item} />
    case "slides":
      return <Slide item={item} />
    case "cards":
      return <CarouselCard item={item} />

    default:
      return null
  }
}

const Carousel = ({
  items,
  type,
  hybrid,
  hideFooterPager,
  forceSameWidthOnAllCards,
  ...props
}) => {
  const [leftVisible, setLeftVisible] = useState(false)
  const [rightVisible, setRightVisible] = useState(true)
  const containerRef = useRef(null)
  const itemContainerRef = useRef(null)
  const [currentPage, setCurrentPage] = useState(0)
  const handleScroll = target => {
    const scrollLeft = Math.round(target.scrollLeft)
    setLeftVisible(scrollLeft > 40)
    setRightVisible(target.scrollWidth - 40 > scrollLeft + target.clientWidth)
    if (scrollLeft + target.clientWidth === target.scrollWidth)
      setCurrentPage(availablePages?.length - 1)
    else
      setCurrentPage(
        availablePages?.indexOf(
          availablePages.find(p => p[0] <= scrollLeft && p[1] > scrollLeft)
        )
      )
  }

  const [availablePages, setAvailablePages] = useState(null)

  const getSpaceParams = () => {
    const windowWidth = window.innerWidth

    const initialOffsetLeft =
      itemContainerRef.current.offsetLeft +
      (containerRef.current.getBoundingClientRect().x ||
        containerRef.current.getBoundingClientRect().left)

    const scrollWidth = containerRef.current.scrollWidth

    return [windowWidth, initialOffsetLeft, scrollWidth]
  }

  const onWindowResize = () => {
    if (!items || items.length < 1) return

    if (type === "slides") {
      setAvailablePages(
        Array.from(itemContainerRef.current.childNodes).map(c => [
          c.offsetLeft,
          c.offsetLeft + c.offsetWidth,
        ])
      )

      return
    }

    const [windowWidth, initialOffsetLeft, scrollWidth] = getSpaceParams()
    let pages = [[0, windowWidth]]
    if (scrollWidth > windowWidth) {
      let offset = initialOffsetLeft

      Array.from(itemContainerRef.current.childNodes).forEach(child => {
        const left = initialOffsetLeft + child.offsetLeft
        const right = offset + child.offsetLeft + child.offsetWidth
        // If item ends between pageStart and pageEnd, don't count a new page
        if (right < pages[pages.length - 1][0] + windowWidth) {
          return
        }
        // Else, previous page ends at the item's left, and new one starts on that point
        pages[pages.length - 1][1] = left - 10
        pages.push([left - 10, left + windowWidth])
      })
    } else {
      pages = []
    }
    setAvailablePages(pages)
  }

  useEffect(() => {
    containerRef && containerRef.current && handleScroll(containerRef.current)
    if (!availablePages) onWindowResize()
    window.addEventListener("resize", onWindowResize)
    return () => window.removeEventListener("resize", onWindowResize)
  })

  const onPagerItemClick = index => {
    containerRef.current.scrollTo({
      left: availablePages[index][0],
      behavior: "smooth",
    })
  }

  const onArrowClick = (scrollLeft = true) => {
    if (!availablePages || availablePages.length === 0) {
      containerRef.current.scrollBy({
        left: 100 * (scrollLeft ? -1 : 1),
        behavior: "smooth",
      })
    }
    const page = availablePages[currentPage + (scrollLeft ? -1 : 1)]
    page &&
      containerRef.current.scrollTo({
        left: page[0],
        behavior: "smooth",
      })
  }
  const isDesktop = useMedia({ minWidth: size.desktop })
  const isTablet = useMedia({ minWidth: size.tablet })
  return (
    <div style={{ position: "relative" }} {...props}>
      {(!hybrid || isTablet) && (
        <S.LeftArrowButton
          onClick={() => onArrowClick()}
          className={` left arrow ${leftVisible && "visible"}`}
        >
          <Arrow />
        </S.LeftArrowButton>
      )}
      <S.ScrollableContainer
        ref={containerRef}
        onScroll={e => handleScroll(e.target)}
        noMarginRight
        noMarginLeft
        hybrid={hybrid}
        fullWidth={type === "slides"}
        disableScroll={type === "slides"}
        {...props}
      >
        <S.HostContainer fullWidth={type === "slides"}>
          <S.ItemHost
            fullWidth={type === "slides"}
            hybrid={hybrid}
            ref={itemContainerRef}
            forceSameWidthOnAllCards={forceSameWidthOnAllCards}
          >
            {items.map((item, i) => (
              <RenderItem
                isDesktop={isDesktop}
                type={type}
                item={item}
                key={i}
              />
            ))}
          </S.ItemHost>
        </S.HostContainer>
      </S.ScrollableContainer>
      {!hideFooterPager && availablePages?.length > 1 && isDesktop && (
        <GridLayout>
          <Pager
            forGridLayout
            onPageClick={i => onPagerItemClick(i)}
            pages={availablePages?.length || 0}
            current={currentPage}
          />
        </GridLayout>
      )}
      {(!hybrid || isTablet) && (
        <S.RightArrowButton
          onClick={() => onArrowClick(false)}
          className={`arrow right ${rightVisible && "visible"}`}
        >
          <Arrow />
        </S.RightArrowButton>
      )}
    </div>
  )
}

Carousel.propTypes = {
  type: PropTypes.oneOf(["articles", "people", "slides", "cards"]).isRequired,
  hybrid: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      img: PropTypes.string,
      fluid: PropTypes.any,
      imgAlt: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      url: PropTypes.string,
    })
  ),
}

export default Carousel
